import { useCallback, useEffect, useState, type FC } from 'react';
import type { LinkPreview } from '../../../domain/models/LinkPreview';
import { LinkPreviewRepositoryImpl } from '../../../infrastructure/repositories/LinkPreviewRepositoryImpl';
import { ReactComponent as ArrowUpShapeLineIcon } from '../../../icons/arrow.up.shape.line.svg';
import { t } from 'i18next';
import GridSkeletons from '../../../components/skeletons/GridSkeletons';

interface LinkPreviewBoxProps {
  url: string;
  onPreviewClick?: (preview: LinkPreview) => void;
}

const linkPreviewRepository = new LinkPreviewRepositoryImpl();

const LinkPreviewBox: FC<LinkPreviewBoxProps> = ({ url, onPreviewClick }) => {
  const [linkPreview, setLinkPreview] = useState<LinkPreview | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchLinkPreview = useCallback(async (url: string) => {
    try {
      setIsLoading(true);
      const preview = await linkPreviewRepository.getLinkPreview(url);
      setLinkPreview(preview);
    } catch {
      setLinkPreview(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (url) {
      fetchLinkPreview(url);
    }
  }, [url, fetchLinkPreview]);

  const handlePreviewClick = () => {
    if (linkPreview && onPreviewClick) {
      onPreviewClick(linkPreview);
    }
  };

  if (isLoading) {
    return <GridSkeletons gridSize={1} skeletonSize={1} skeletonHeight={'80px'} />;
  }

  return (
    <>
      {linkPreview && (
        <div
          className="flex flex-row justify-between border rounded-[16px] border-surface-lv2"
          onClick={handlePreviewClick}
        >
          <div className="flex flex-col overflow-hidden justify-center pl-[18px]">
            <p className="text-text-main-lv3 font-medium text-[13px] max-w-[180px] leading-[16px] text-left truncate">
              {linkPreview.title}
            </p>
            <p className="text-text-sub-lv1 text-[11px] leading-[14px] mt-[3px] max-w-[180px] truncate">
              {linkPreview.getDescriptionWithoutLink()}
            </p>
            <div className="flex flex-row items-center gap-[2px] mt-[5px]">
              <p className="font-medium text-[12px] leading-[14px] text-text-primary">
                {t('LinkPreviewBox.navigateLink')}
              </p>
              <ArrowUpShapeLineIcon className="fill-text-primary w-[14px] h-[14px]" />
            </div>
          </div>
          <img
            src={linkPreview.image}
            alt={linkPreview.title}
            className="w-[76px] h-[76px] rounded-tr-[8px] rounded-br-[8px]"
          />
        </div>
      )}
    </>
  );
};

export default LinkPreviewBox;
